/**
 * Dev-only live readout of the consent manager and advid state, shown in the admin panel. Answers
 * the QA question "what is the consent layer telling consumers right now?" without digging through
 * cookies, local storage or network requests.
 *
 * Reads only the vendor-neutral consent manager rather than Cookiebot, so it shows what consumers
 * actually see — including the cases where Cookiebot has not answered and everything reads as
 * declined.
 */
import { useEffect, useState } from 'react';
import { PanelContainer } from '@tv2-cas/offspring/admin/AdminPanel';
import { ADVID_HEADER, ADVID_STORAGE_KEY, getAdvid, getAdvidHeaders } from '@tv2/lib-consent/advid';
import {
  CONSENT_CATEGORIES,
  type ConsentCategory,
  areAdvertisingIdentifiersAllowed,
  getConsentDetails,
  getConsentTrackingStatus,
  getCurrentConsentDecision,
  getTcfConsentString,
  isConsentIntegrationEnabled,
} from '@tv2/lib-consent/consentManager';
import { isConsentCategoryAllowed } from '@tv2/lib-consent/consentPolicy';
import { getPersistedConsentStatus } from '@tv2/lib-offspring/libs/consentStatus';

/** Every category on the manager. The IAB groups are always declined on the web (Cookiebot). */
const CATEGORIES: ReadonlyArray<ConsentCategory> = CONSENT_CATEGORIES;

function readPersistedAdvid(): string | null {
  try {
    return localStorage.getItem(ADVID_STORAGE_KEY);
  } catch {
    return null;
  }
}

const Row = ({ label, on, detail }: { label: string; on: boolean; detail?: string }) => (
  <div className="flex items-center py-1">
    <span
      className={`inline-block w-3 h-3 rounded-full mr-3 shrink-0 ${on ? 'bg-green-500' : 'bg-red-500'}`}
    />
    <span className="text-white/90">{label}</span>
    {detail ? <span className="ml-2 text-white/50 break-all">{detail}</span> : null}
  </div>
);

export function ConsentDebugPanel() {
  // The manager is a module singleton outside React, so there is nothing to subscribe to.
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(tick => tick + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const integrationEnabled = isConsentIntegrationEnabled();
  const decision = getCurrentConsentDecision();
  const exposedAdvid = getAdvid();
  const persistedAdvid = readPersistedAdvid();
  const advidHeader = getAdvidHeaders()[ADVID_HEADER];
  const consentStatus = getConsentTrackingStatus();
  const persistedConsentStatus = getPersistedConsentStatus();
  const advidAllowed = areAdvertisingIdentifiersAllowed();
  const tcfString = getTcfConsentString();

  const categoryFlags = CATEGORIES.map(
    category => `${category}:${isConsentCategoryAllowed(category) ? '1' : '0'}`
  ).join(' ');

  return (
    <PanelContainer>
      <div className="text-body-s">
        <div className="mb-3 font-bold text-white">Consent debug</div>

        <Row
          label="CMP integration"
          on={integrationEnabled}
          detail={integrationEnabled ? 'Cookiebot' : '(no CMP configured — everything allowed)'}
        />
        <Row
          label="Decision recorded"
          on={decision !== null}
          detail={decision ? JSON.stringify(decision) : '(none)'}
        />
        <Row label="Categories as consumers read them" on detail={categoryFlags} />

        <div className="my-3 border-t border-white/20" />

        <Row
          label="Advertising identifiers allowed"
          on={advidAllowed}
          detail={
            advidAllowed
              ? 'full consent — every category granted'
              : '(not every category granted — advid deleted)'
          }
        />
        <Row
          label="advid"
          on={Boolean(exposedAdvid)}
          detail={
            exposedAdvid
              ? `${exposedAdvid.slice(0, 8)}… exposed`
              : persistedAdvid
                ? `${persistedAdvid.slice(0, 8)}… persisted but withheld`
                : '(none)'
          }
        />
        <Row
          label={`${ADVID_HEADER} header`}
          on={Boolean(advidHeader)}
          detail={advidHeader ? 'sent on graph queries' : '(omitted)'}
        />
        <Row
          label="consentStatus on Adobe tracking"
          on={consentStatus === 'accepted'}
          detail={`${consentStatus} (key always present) — stored: ${persistedConsentStatus ?? '(nothing)'}`}
        />
        <Row
          label="consentDetails on Adobe tracking"
          on={getConsentDetails()?.includes(':1') ?? false}
          detail={getConsentDetails() ?? '(undefined — omitted, no CMP)'}
        />
        <Row
          label="TCF consent string"
          on={Boolean(tcfString)}
          detail={tcfString ? `${tcfString.slice(0, 22)}…` : '(undefined — no TCF signal)'}
        />
      </div>
    </PanelContainer>
  );
}
